import { MobxLitElement } from '@adobe/lit-mobx';
import type { PortalEvent } from '@src/lit/portal.js';
import { html, nothing, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { runInAction } from 'mobx';
import { CompVisibleMixin } from '@common/lit/mixins/CompVisibleMixin.js';
import { TemplatingMixin } from '@common/lit/mixins/TemplatingMixin.js';
import { Theme } from '@common/types/enum.js';
import { DisplayMode, Generation } from '@api-consent/schema/Enums.js';
import { Tracker } from '@src/classes/Tracker.js';
import { ReplaceElementsMixin } from '@src/components/mixins/ReplaceElementsMixin.js';
import { templates } from '@src/components/templates/index.js';
import { comsStore } from '@src/state/ComsStore.js';
import { ConsentGroupStore } from '@src/state/ConsentGroupStore.js';
import { TefComs } from '@src/types/typings.js';
import { Service } from '@src/util/Service.js';
import '@src/components/TefComsTriStateButton.js';
import '@src/components/TefComsPagination.js';
import '@src/components/tef-coms-customer-attribute.js';

const CompositeClass = CompVisibleMixin(ReplaceElementsMixin(TemplatingMixin(MobxLitElement, Theme.OCCL, templates)));

/**
 * The custom element `<tef-coms4cs>`.
 */
export class TefComs4csMicroFrontend extends CompositeClass {
  @property({ type: String }) apiUri = '';
  @property({ type: String }) agentToken = '';
  @property({ type: String }) gatewayToken = '';
  @property({ type: String }) lifecycle = '';
  @property({ type: String }) mode = '';
  @property({ type: String }) traceid = '';
  @property({ type: String }) occlUri = '';
  @property({ type: String }) styleContext = '';
  @property({ type: String }) consentwhitelist = '';
  @property({ type: String }) channel = '';
  @property({ type: String }) usertype = '';
  @property({ type: String }) vonr = '';
  @property({ type: String }) references = '';
  @property({ type: Boolean }) showReferences = false;
  @property({ type: Boolean }) showVersions = false;
  @property({ type: Boolean }) isExistingRegister = false;
  @property({ type: Boolean }) isNewCustomer = false;
  @property({ type: String }) spId = '';
  @property({ type: String }) lcId = '';
  @property({ type: String }) registerId = '';
  @property({ type: String }) fetch = '';
  @property({ type: String }) group = '';
  @property({ type: String }) sessionkey = '';

  eventTracking: Record<string, number> = {};

  store = comsStore;

  nodeTreeCompletelyRendered = false;
  private service = new Service(this); // binding service to <tef-coms4cs>
  private tracker = new Tracker();

  async connectedCallback(): Promise<void> {
    this.setTemplatingTheme(this.occlUri ? Theme.OCCL : Theme.TEFONE);

    try {
      comsStore.reset();
      // show more infos if debugmode is on
      /* v8 ignore if -- @preserve */
      if (localStorage.getItem('tef-coms-4cs-debug-mode')) {
        let timestampInit = 0;
        let timestampReady = 0;

        this.addEventListener('tef-coms4cs', (e) => {
          const { detail } = e as TefComs.Event;
          const channel = detail.level === 'error' ? 'error' : 'info';
          // eslint-disable-next-line no-console
          console[channel]('tef-coms: event', detail.name, e);
          if (detail.name === 'config/INIT') {
            timestampInit = (performance && performance.now) ? performance.now() : 0;
          }
          if (detail.name === 'load/READY') {
            timestampReady = (performance && performance.now) ? performance.now() : 0;
            const infoVersion = `version ${TefComs4csMicroFrontend.version}`;
            const infoReady = `Micro-frontend ready in ${Math.trunc(timestampReady - timestampInit)} ms, `;
            // eslint-disable-next-line no-console
            console.log(infoReady + infoVersion);
          }
        });
      }
      // issue 'config/INIT' immediately because it is used for performance measurements
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'config/INIT',
        level: 'info',
        message: 'micro-frontend is initializing',

      });
      super.connectedCallback(); // start rendering immediately !!

      await this.store.init({
        apiUri: this.apiUri,
        agentToken: this.agentToken,
        gatewayToken: this.gatewayToken,
        lifecycle: this.lifecycle,
        mode: this.mode,
        traceid: this.traceid,
        occlUri: this.occlUri,
        styleContext: this.styleContext,
        consentwhitelist: this.consentwhitelist,
        channel: this.channel,
        usertype: this.usertype,
        vonr: this.vonr,
        references: this.references,
        showReferences: this.showReferences,
        showVersions: this.showVersions,
        isExistingRegister: this.isExistingRegister,
        isNewCustomer: this.isNewCustomer,
        spId: this.spId,
        lcId: this.lcId,
        registerId: this.registerId,
        fetch: this.fetch,
        group: this.group,
        generation: Generation.CLASSIC,
        sessionkey: this.sessionkey,
      });

      this.store.setGrantedValueForGroups();

      if (this.occlUri) {
        this.shadowRoot?.addEventListener(
          'occl.collapsible.open',
          (event) => {
            const { id: name } = <HTMLDivElement>event?.target;
            this.tracker.clickCollapsable(name);
            const consentGroup = this.store.findGroupByName(name);
            if (consentGroup) {
              runInAction(() => {
                consentGroup.collapsibleOpen = true;
              });
            }
          },
        );

        const { history } = comsStore;
        this.shadowRoot?.addEventListener(
          'occl.pager.open',
          (event) => history?.gotoPage((<CustomEvent>event).detail as number) as void,
        );
      }
      this.attachCustomerAttributeModalRenderedHandler();

      // Observe this element getting visible/invisible in regards to the viewport when in "group" mode
      if (this.group) {
        this.installIntersectionObserver();
      }

      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (_error) {
      // ignore -- has error condition in store
    }
  }

  attachCustomerAttributeModalRenderedHandler() {
    // If the customer attribute "portal" is rendered we need to substitute stuff
    // like we do in "updated" here.
    // This event is triggered by MFEC -> portal.ts
    this.renderRoot.addEventListener('portal-rendered', (event: unknown) => {
      const modalElement = (event as PortalEvent).detail.contentElement;
      if (modalElement) {
        this.replaceElementsInNode(modalElement);
      }
    });
  }

  componentBecameVisible() {
    this.updateRenderedConsentVersionIds();
  }

  updated() {
    // WPCSCM-1183: tefOne <one-popover> elements used here don't stop the event propagation by themselves.
    //              This causes a click on the pop over to also trigger the consent <one-checkbox> to be clicked resulting
    //              in a wrongly granted consent.
    if (!this.nodeTreeCompletelyRendered && this.occlUri === '') {
      const onePopovers$ = this.renderRoot.querySelectorAll('one-popover');

      // Assume all popovers are rendered once we have at least 1
      if (onePopovers$.length > 0) {
        this.nodeTreeCompletelyRendered = true;

        const handler = (event : Event) => event.stopPropagation();
        onePopovers$.forEach((el$) => {
          el$.addEventListener('click', handler);
        });
      }
    }
  }

  /**
   * returns filtered list of consentGroup for rendering.
   *
   * Either returns the complete list or - in case of property "group" usage - the requested consent groups
   */
  get displayConsentGroups() {
    // We might get called to early
    if (!this.store.consentGroups) {
      return [];
    }

    // No "groups" attribute
    if (!this.group || this.group === '') {
      return this.store.consentGroups;
    }

    const finalGroups: ConsentGroupStore[] = [];
    const groups: string[] = this.group.split(',').map((g) => g.toLowerCase().trim());

    // Put groups into final in the wanted order
    groups.forEach((groupName) => {
      const consentGroup = this.store.consentGroups?.find(
        (foundGroup) => foundGroup.name?.toLowerCase().trim() === groupName,
      );

      if (consentGroup) {
        // Add consent to list and also add the generation
        finalGroups.push(consentGroup);
      }
    });

    // No groups found -> empty
    if (finalGroups.length === 0 && !this.eventTracking['consents/EMPTY']) {
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'consents/EMPTY',
        level: 'info',
        message: 'no consents to be rendered (groups)',
      });
      this.eventTracking['consents/EMPTY'] = 1;
      return [];
    }

    return finalGroups;
  }

  /**
  * Mark all consent version ids inside a consent group as rendered/processed.
  */
  addRenderedConsentGroup(consentGroup: ConsentGroupStore) {
    consentGroup.consents.forEach((consent) => {
      this.store.addRenderedConsentVersionId(consentGroup.name, consent.id);
    });
  }

  /**
  * Remove all consent version ids inside a consent group from the list of rendered/processed consents.
  */
  removeRenderedConsentGroup(consentGroup: ConsentGroupStore) {
    consentGroup.consents.forEach((consent) => {
      this.store.removeRenderedConsentVersionId(consent.id);
    });
  }

  static get version(): string {
    return _MFE_VERSION; // Filled/replaced by esbuild (see scripts/esbuild/*.ts)
  }

  async save(): Promise<void> {
    await this.store.save();
  }

  async undo() {
    this.store.undo();
    return Promise.resolve();
  }

  cancel() {
    this.store.cancel();
  }

  getConsents(): TefComs.ShortConsent[] {
    return this.store.getShortConsents();
  }

  getConsentStatus(): TefComs.ConsentAndCustomerAttributeSaveType[] {
    return this.store.getConsentStatus();
  }

  getReferenceLength(): number {
    return this.store.references?.length || 0;
  }

  updateRenderedConsentVersionIds() {
    if (this.displayConsentGroups) {
      this.displayConsentGroups.forEach((consentGroup) => {
        // For "hidden" groups that have NO grantedDefault -> remove them from the list of "seen" consent version ids
        // See page config for group "iban-hidden" in live-config
        if (consentGroup.displayMode === DisplayMode.HIDDEN && typeof consentGroup.grantedDefault === 'undefined') {
          this.removeRenderedConsentGroup(consentGroup);
        } else {
          this.addRenderedConsentGroup(consentGroup);
        }
      });
    }
  }

  render(): TemplateResult {
    // If fatal error condition (not recoverable), render nothing.
    // Render stylesheet link as early as possible (for performance reasons).
    // Prevent FOUC effect after loading the OCCL resources.
    if (this.store.fatal) {
      return html`<!-- fatal error condition -->`;
    }

    if (!this.displayConsentGroups) {
      return html`${nothing}`;
    }

    const isLoading = (!this.store.isDataLoaded && this.store.client?.processing)
    || (this.store.occl && !this.store.occl.ready);

    // Update the rendered version ids only once
    if (!isLoading) {
      this.updateRenderedConsentVersionIds();
    }

    /* eslint-disable @typescript-eslint/indent */
    return html`<one-stack name="tef-coms4cs">
      ${this.store.occl?.renderStylesheet()}
      ${isLoading
        ? nothing
        : this.getTemplateFn('mainTemplate')()
      }
      ${this.mode.includes('buttons')
       ? this.getTemplateFn('buttonsTemplate')() : nothing}
      </one-stack>`;
  }
}
